// NEW_FILE_JUSTIFIED: Consolidated insurance settings page replacing RenewalSettingsDialog and PaymentSettingsDialog
import { useSearchParams } from 'react-router-dom';
import { useOrgContext } from '@/hooks/useOrgContext';
import { useRenewalSettings } from '@/hooks/useRenewalSettings';
import { usePaymentSettings } from '@/hooks/usePaymentSettings';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { RenewalEmailConfigTab } from '@/components/insurance/RenewalEmailConfigTab';
import { RenewalSmsConfigTab } from '@/components/insurance/RenewalSmsConfigTab';
import { InsuranceSettingsRenewalSection } from '@/components/insurance/settings/InsuranceSettingsRenewalSection';
import { InsuranceSettingsPaymentSection } from '@/components/insurance/settings/InsuranceSettingsPaymentSection';
import { Settings } from 'lucide-react';
import { toast } from 'sonner';

export function InsuranceSettingsPage() {
  const { currentOrg, isLoading: orgLoading } = useOrgContext();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') ?? 'email-config';

  const orgId = currentOrg?._id;

  const {
    state: renewalState,
    dispatch: renewalDispatch,
    saveAll: saveRenewal,
    isLoading: isRenewalLoading,
  } = useRenewalSettings(orgId!); // AS_JUSTIFIED: orgId guard below prevents rendering when undefined

  const {
    state: paymentState,
    dispatch: paymentDispatch,
    saveAll: savePayment,
    isLoading: isPaymentLoading,
  } = usePaymentSettings(orgId!); // AS_JUSTIFIED: orgId guard below prevents rendering when undefined

  if (!orgLoading && !currentOrg) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <Settings className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">No organisation found</h3>
          <p className="text-sm text-muted-foreground">
            You need to be part of an organisation to manage insurance settings.
          </p>
        </div>
      </div>
    );
  }

  if (orgLoading || !currentOrg) return null;

  const isLoading = isRenewalLoading || isPaymentLoading;
  const isSaving = renewalState.isSaving || paymentState.isSaving;

  const handleSave = async () => {
    try {
      await Promise.all([saveRenewal(), savePayment()]);
      toast.success('Settings saved');
    } catch {
      toast.error('Failed to save settings');
    }
  };

  return (
    <div className="min-h-full bg-background-secondary py-8 px-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Insurance Settings</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Configure notification providers, templates, and thresholds.
          </p>
        </div>
        <Button onClick={() => void handleSave()} disabled={isSaving || isLoading}>
          {isSaving ? 'Saving...' : 'Save all'}
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-24 text-sm text-muted-foreground">
          Loading settings...
        </div>
      ) : (
        <div className="bg-table-container border border-table-container-border rounded-none">
          <Tabs defaultValue={defaultTab}>
            <div className="border-b border-table-row-border px-6 pt-4 pb-0">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="email-config">Email Config</TabsTrigger>
                <TabsTrigger value="sms-config">SMS Config</TabsTrigger>
                <TabsTrigger value="renewal">Renewal</TabsTrigger>
                <TabsTrigger value="payments">Payments</TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="email-config" className="p-6">
              <RenewalEmailConfigTab
                emailConfig={{
                  provider: renewalState.emailProvider,
                  gmailUser: renewalState.gmailUser || undefined,
                  gmailAppPassword: renewalState.gmailAppPassword || undefined,
                }}
                onEmailConfigChange={config => {
                  renewalDispatch({ type: 'SET_EMAIL_PROVIDER', value: config.provider });
                  renewalDispatch({ type: 'SET_GMAIL_USER', value: config.gmailUser ?? '' });
                  renewalDispatch({
                    type: 'SET_GMAIL_APP_PASSWORD',
                    value: config.gmailAppPassword ?? '',
                  });
                }}
                orgId={currentOrg._id}
              />
            </TabsContent>

            <TabsContent value="sms-config" className="p-6">
              <RenewalSmsConfigTab
                smsConfig={
                  renewalState.websmsApiKey || renewalState.senderName
                    ? {
                        websmsApiKey: renewalState.websmsApiKey,
                        senderName: renewalState.senderName,
                      }
                    : undefined
                }
                onSmsConfigChange={config => {
                  renewalDispatch({ type: 'SET_WEBSMS_API_KEY', value: config.websmsApiKey });
                  renewalDispatch({ type: 'SET_SENDER_NAME', value: config.senderName });
                }}
                orgId={currentOrg._id}
              />
            </TabsContent>

            <TabsContent value="renewal">
              <InsuranceSettingsRenewalSection
                renewalState={renewalState}
                renewalDispatch={renewalDispatch}
              />
            </TabsContent>

            <TabsContent value="payments">
              <InsuranceSettingsPaymentSection
                paymentState={paymentState}
                paymentDispatch={paymentDispatch}
              />
            </TabsContent>
          </Tabs>
        </div>
      )}
    </div>
  );
}
